import * as React from "react"

//material
import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import { Button, Card, CardContent, TextField } from "@mui/material"
import { LoadingButton } from "@mui/lab"

//formik
import { Formik } from "formik"
import * as Yup from "yup"

//router
import {useNavigate, useLocation} from "react-router-dom"

//apollo
import { useMutation } from "@apollo/client"
import { CREATE_GLOSSARY, LOAD_ALL_GLOSSARIES, UPDATE_GLOSSARY } from "../../../queries/admin/glossaryQueries"

//components
import { ErrorNotification, SuccessNotification } from "../../../components/Notifications/Notifications"

//utils
import { getFormErrors } from "../../../utils/errorsUtils"

//styles
import quizLayoutStyles from "../../../assets/styles/quizLayoutStyles"

export default function AdminGlossaryForm() {
    const navigate = useNavigate()
    const location = useLocation()

    const updateItem = location.state?.updateItem

    const notifySuccess = message => SuccessNotification(message)
    const notifyError = message => ErrorNotification(message)

    const [createGlossary, {loading: loadingCreate}] = useMutation(CREATE_GLOSSARY, {
        refetchQueries: [ LOAD_ALL_GLOSSARIES ]
    })
    const [updateGlossary, {loading: loadingUpdate}] = useMutation(UPDATE_GLOSSARY, {
        refetchQueries: [ LOAD_ALL_GLOSSARIES ]
    })

    const successForm = () => {
        updateItem ? notifySuccess("Word definition updated") : notifySuccess("Word definition added")
        navigate("/quiz/manage-glossary")
    }

    const FormHeaderBar = () => {
        return (
            <AppBar position="static" color={"transparent"}>
                <Toolbar disableGutters sx={quizLayoutStyles.componentHeaderToolbar}>
                    <Box>
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                mr: 2,
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                letterSpacing: '.3rem',
                                color: 'inherit',
                                textDecoration: 'none',
                            }}
                        >
                            {
                                updateItem ? "Edit definition" : "Create definition"
                            }
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>
        );
    };

    //formik
    const initialValues = {
        word: updateItem ? updateItem.word : "",
        meaning: updateItem ? updateItem.meaning : "",
    }

    const validationSchema = Yup.object({
        word: Yup.string().required('field required'),
        meaning: Yup.string().required('field required'),
    })

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(data, { setErrors }) => {
                const register = updateItem ? updateGlossary : createGlossary
                register({
                    variables: {
                        id: updateItem?.id ?? null,
                        word: data.word,
                        meaning: data.meaning
                    }
                })
                    .then((response) => {
                        const returned = updateItem ? response?.data?.updateGlossary : response?.data?.createGlossary
                        if (returned.glossary) {
                            successForm()
                        } else {
                            notifyError("Operation on glossary failed, try again")
                        }
                    })
                    .catch(error => {
                        if (error.graphQLErrors.length) {
                            getFormErrors(error.graphQLErrors, notifyError, setErrors)
                        } else {
                            notifyError("Operation on glossary failed, try again")
                        }
                    })
            }}
        >
            {
                formik => (
                    <Box sx={{width: "100%"}}>
                        <Card variant={"outlined"} sx={quizLayoutStyles.componentCardContainer}>
                            <FormHeaderBar/>
                            <CardContent sx={{ maxHeight: "calc(45vh)", overflowY: "auto" }}>
                                <form onSubmit={formik.handleSubmit}>
                                    <Box sx={{display: "flex", flexWrap: 'wrap', width: "100%"}}>
                                        <TextField
                                            name={"word"}
                                            label="Word"
                                            variant={"filled"}
                                            size={"small"}
                                            fullWidth
                                            sx={{ m: 1 }}
                                            {...formik.getFieldProps("word")}
                                            error={formik.errors.word && formik.touched.word}
                                            helperText={(formik.errors.word && formik.touched.word) && formik.errors.word}
                                        />

                                        <TextField
                                            name={"meaning"}
                                            label="Definition"
                                            variant={"filled"}
                                            size={"small"}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            sx={{ m: 1 }}
                                            {...formik.getFieldProps("meaning")}
                                            error={formik.errors.meaning && formik.touched.meaning}
                                            helperText={(formik.errors.meaning && formik.touched.meaning) && formik.errors.meaning}
                                        />
                                    </Box>

                                    <Box sx={{display: "flex", justifyContent: "right", p: 1, m: 1}}>
                                        <Button
                                            color={"secondary"}
                                            variant={"contained"}
                                            size={"small"}
                                            sx={{m: 1}}
                                            onClick={() => navigate("/quiz/manage-glossary")}
                                        >
                                            Cancel
                                        </Button>

                                        <LoadingButton
                                            type={"submit"}
                                            color={"primary"}
                                            variant={"contained"}
                                            size={"small"}
                                            sx={{m: 1}}
                                            disabled={!formik.isValid}
                                            loading={loadingCreate || loadingUpdate}
                                        >
                                            {updateItem ? "Update" : "Create"}
                                        </LoadingButton>
                                    </Box>
                                </form>
                            </CardContent>
                        </Card>
                    </Box>
                )
            }
        </Formik>
    )
}